import { useQuery } from '@apollo/client'
import { BasicLayout } from '@components/layouts/basic-layout'
import { BaseAccordion, BasePanel } from '@components/base-accordion'
import { BaseBlock } from '@components/base-block'
import { TextButton } from '@components/base-button'
import { BaseCell } from '@components/base-cell'
import { BaseGrid } from '@components/base-grid'
import { BaseTab, BaseTabs } from '@components/base-tabs'
import { tabOverrides } from '@components/base-tabs/base-tab'
import { HeadingM, LabelM } from '@components/base-typography'
import { InfoBoxWithIcon } from '@components/info-box-with-icon'
import {
  FaqPartsFragment,
  GetAllFaqCategoriesDocument,
} from '@lib/graphql/documents.generated'
import { Routes } from '@lib/routes'
import { colors } from '@styles/colors'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Key, useEffect, useMemo, useState } from 'react'

const EditorViewer = dynamic(() => import('@components/editor/editor-viewer'), {
  ssr: false,
})

interface FaqPageProps {}

const FaqPage: NextPage<FaqPageProps> = () => {
  const { data, loading } = useQuery(GetAllFaqCategoriesDocument)

  const [activeKey, setActiveKey] = useState<Key>(1)
  const [currentList, setCurrentList] = useState<FaqPartsFragment[]>()

  useEffect(() => {
    if (loading || !data?.allFaqs || data.allFaqs.length === 0) return
    setCurrentList(data.allFaqs[0].faqs || [])
  }, [data?.allFaqs, loading])

  const accordions = useMemo(
    () =>
      currentList?.map(({ id, title, description }) => (
        <BasePanel title={title} key={id}>
          <EditorViewer initialValue={description || ''} />
        </BasePanel>
      )),
    [currentList]
  )

  return (
    <>
      <BaseGrid spaceTop={[24, 48, 72]}>
        <BaseCell span={[4, 6, 8]} skip={[0, 1, 0]}>
          <HeadingM>자주 묻는 질문</HeadingM>
          <BaseBlock marginTop="40px" marginBottom="40px">
            <BaseTabs
              activeKey={activeKey}
              onChange={({ activeKey }) => {
                const current = data?.allFaqs.find(
                  (faq) => faq.id === Number(activeKey)
                )
                if (current?.faqs) {
                  setCurrentList(current.faqs)
                }
                setActiveKey(activeKey)
              }}
              activateOnFocus
            >
              {!loading &&
                data?.allFaqs.map(({ id, name }) => {
                  return (
                    <BaseTab key={id} title={name} overrides={tabOverrides}>
                      <BaseAccordion stateReducer={(_, nextState) => nextState}>
                        {accordions}
                      </BaseAccordion>
                    </BaseTab>
                  )
                })}
            </BaseTabs>
          </BaseBlock>
        </BaseCell>
        <BaseCell span={[4, 3, 3]} skip={[0, 1, 1]} spaceTop={[72, 72, 72]}>
          <InfoBoxWithIcon icon="Headset">
            <LabelM whiteSpace="pre-wrap">{`문제가 해결되지 않았다면\n1대1 문의로 질문해주세요.`}</LabelM>
            <Link href={Routes.SUPPORT_INQUIRY_WRITE} passHref>
              <TextButton
                spaceTop={20}
                trailing="ChevronRight"
                color={colors.blue400}
              >
                문의하기
              </TextButton>
            </Link>
          </InfoBoxWithIcon>
        </BaseCell>
      </BaseGrid>
      <EditorViewer initialValue="" />
    </>
  )
}

FaqPage.getLayout = (page) => (
  <BasicLayout helmetTitle="Ontol | 자주묻는질문">{page}</BasicLayout>
)

export default FaqPage
